import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface HesselsGithubApi {
  listRepos(org?: string): Promise<RepoSummary[]>;
  listBranchRulesets(org?: string): Promise<BranchRulesetSummary[]>;
  listBranchRulesetsForRepos(repos: string[], org?: string, branch?: string): Promise<BranchRulesetSummary[]>;
  applyBranchRulesets(req: ApplyBranchRulesetRequest): Promise<ApplyBranchRulesetResponse>;
}

export interface GitHubRulesetRule {
  type: string;
  parameters?: Record<string, any>;
}

export interface RepoSummary {
  id: number;
  name: string;
  fullName: string;
  private: boolean;
  visibility: string;
  defaultBranch: string;
  archived: boolean;
  size: number;
  pushedAt: string;
  updatedAt: string;
  createdAt: string;
  htmlUrl: string;
  description?: string | null;
  language?: string | null;
}

export interface BranchRulesetSummary {
  repo: string; // full name (owner/name)
  branch: string; // branch name examined
  defaultBranch: string;
  protected: boolean;
  rules?: GitHubRulesetRule[]; // effective rules returned by GitHub for the branch
  error?: { status?: number; message?: string } | undefined; // present if retrieval failed or permission issue
}

export interface ApplyBranchRulesetRequest {
  org?: string;
  branch?: string; // optional override; if absent use each repo's default
  repos: string[]; // repo names (not full owner/name) OR full names? We'll accept names, backend infers org
  rules?: GitHubRulesetRule[]; // optional override; backend fills defaults if omitted
  dryRun?: boolean; // simulate only
}

export interface ApplyBranchRulesetResponse {
  results: Array<{
    repo: string;
    branch: string;
    ok: boolean;
    message?: string;
  }>;
}

export const hesselsGithubApiRef = createApiRef<HesselsGithubApi>({
  id: 'plugin.hessels-github.service',
});

export class HesselsGithubClient implements HesselsGithubApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async listRepos(org?: string): Promise<RepoSummary[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('hessels-github');
    // Identity token not required for internal backend route currently
    const token = undefined;
    const params = org ? `?org=${encodeURIComponent(org)}` : '';
    const resp = await this.fetchApi.fetch(`${baseUrl}/repos${params}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!resp.ok) {
      throw new Error(
        `Failed to fetch repos: ${resp.status} ${resp.statusText}`,
      );
    }
    const body = await resp.json();
    return body.repositories as RepoSummary[];
  }

  async listBranchRulesets(org?: string): Promise<BranchRulesetSummary[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('hessels-github');
    const params = org ? `?org=${encodeURIComponent(org)}` : '';
    const resp = await this.fetchApi.fetch(`${baseUrl}/branch-rulesets${params}`);
    if (!resp.ok) {
      throw new Error(`Failed to fetch branch rulesets: ${resp.status} ${resp.statusText}`);
    }
    const body = await resp.json();
    return body.items as BranchRulesetSummary[];
  }

  async listBranchRulesetsForRepos(repos: string[], org?: string, branch?: string): Promise<BranchRulesetSummary[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('hessels-github');
    const params = new URLSearchParams();
    if (org) params.set('org', org);
    if (branch) params.set('branch', branch);
    if (repos.length) params.set('repos', repos.join(','));
    const resp = await this.fetchApi.fetch(`${baseUrl}/branch-rulesets?${params.toString()}`);
    if (!resp.ok) throw new Error(`Failed to fetch branch rulesets subset: ${resp.status} ${resp.statusText}`);
    const body = await resp.json();
    return body.items as BranchRulesetSummary[];
  }

  async applyBranchRulesets(req: ApplyBranchRulesetRequest): Promise<ApplyBranchRulesetResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('hessels-github');
    const resp = await this.fetchApi.fetch(`${baseUrl}/branch-rulesets/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!resp.ok) {
      throw new Error(`Failed to apply branch rulesets: ${resp.status} ${resp.statusText}`);
    }
    return await resp.json() as ApplyBranchRulesetResponse;
  }
}
